import { createContext, useContext, useEffect, useState } from "react";
import * as React from "react";
import axios from "axios";
import {
  LibraryThingEnrichment,
  SyndeticsUnboundUtil,
} from "@tlc/syndetics-util";

export interface SyndeticsConfig {
  active: boolean;
  accountNumber: string;
  clientCode: string;
  instanceId: string;
  branchFilters: string[];
}

interface SyndeticsConfigContextType {
  syndeticsConfig: SyndeticsConfig;
  enrichmentsEnabled: LibraryThingEnrichment[];
  hasRelatedTabEnrichments: boolean;
  hasReviewsTabEnrichments: boolean;
  hasMoreInfoTabEnrichments: boolean;
  hasSummaryEnrichment: boolean;
  util: SyndeticsUnboundUtil;
  relatedTabEnrichments: LibraryThingEnrichment[];
  moreInfoTabEnrichments: LibraryThingEnrichment[];
}

const SyndeticsConfigContext = createContext<SyndeticsConfigContextType>(null);

export const useSyndeticsConfig = () => {
  const context = useContext(SyndeticsConfigContext);

  if (context === undefined) {
    throw new Error(
      "useSyndeticsConfig must be used within a SyndeticsConfigProvider"
    );
  }

  return context;
};

class SyndeticsClass implements SyndeticsConfigContextType {
  public relatedTabEnrichments = [
    LibraryThingEnrichment.AlternateSeries,
    LibraryThingEnrichment.AltmetricBadges,
    LibraryThingEnrichment.RCLCoreTitles,
    LibraryThingEnrichment.Tags,
    LibraryThingEnrichment.YouMayAlsoLike,
    LibraryThingEnrichment.Lists,
    LibraryThingEnrichment.AlsoAvailableAs,
    LibraryThingEnrichment.Series,
  ];

  public moreInfoTabEnrichments = [
    LibraryThingEnrichment.Summary,
    LibraryThingEnrichment.VideoGameSummary,
    LibraryThingEnrichment.VideoAndMusicSummary,
    LibraryThingEnrichment.Awards,
    LibraryThingEnrichment.LookInside,
    LibraryThingEnrichment.BookProfile,
    LibraryThingEnrichment.AboutTheAuthor,
  ];

  constructor(
    public syndeticsConfig: SyndeticsConfig,
    public enrichmentsEnabled: LibraryThingEnrichment[],
    public util: SyndeticsUnboundUtil
  ) {}

  get hasRelatedTabEnrichments() {
    return this.enrichmentsEnabled.some((enrichment) =>
      this.relatedTabEnrichments.includes(enrichment)
    );
  }

  get hasMoreInfoTabEnrichments() {
    return this.enrichmentsEnabled.some((enrichment) =>
      this.moreInfoTabEnrichments.includes(enrichment)
    );
  }

  get hasReviewsTabEnrichments() {
    return this.enrichmentsEnabled.some((enrichment) =>
      [LibraryThingEnrichment.ProfessionalReviews].includes(enrichment)
    );
  }

  get hasSummaryEnrichment() {
    return !!this.enrichmentsEnabled.find(
      (e) => e === LibraryThingEnrichment.Summary
    );
  }
}

// TODO update LibraryThingWidget to use SyndeticsConfigHolder or replace it
export const SyndeticsConfigHolder = (() => {
  const syndeticsUnboundUtil = new SyndeticsUnboundUtil();
  // TODO check location config instance handling

  let syndeticsConfig: SyndeticsConfig = null;
  let enrichmentsEnabled: LibraryThingEnrichment[] = null;

  const fetchConfig = async () => {
    const { data } = await axios.get<SyndeticsConfig>("/syndetics-config");
    syndeticsConfig = data;

    if (data.active === false) {
      enrichmentsEnabled = [];
      return new SyndeticsClass(
        syndeticsConfig,
        enrichmentsEnabled,
        syndeticsUnboundUtil
      );
    }

    const { accountNumber, instanceId } = data;
    try {
      enrichmentsEnabled = await syndeticsUnboundUtil.initScript(
        accountNumber,
        instanceId
      );
    } catch (e: unknown) {
      console.error("Error getting enabled enrichments: ", e);
      enrichmentsEnabled = [];
    } finally {
      return new SyndeticsClass(
        syndeticsConfig,
        enrichmentsEnabled,
        syndeticsUnboundUtil
      );
    }
  };

  let pendingRequest: Promise<SyndeticsConfigContextType> | null = null;
  async function getData() {
    if (pendingRequest) {
      return pendingRequest;
    }

    if (syndeticsConfig && enrichmentsEnabled) {
      return new SyndeticsClass(
        syndeticsConfig,
        enrichmentsEnabled,
        syndeticsUnboundUtil
      );
    }
    pendingRequest = fetchConfig();
    return pendingRequest;
  }

  function getCurrentValue() {
    return new SyndeticsClass(
      syndeticsConfig,
      enrichmentsEnabled,
      syndeticsUnboundUtil
    );
  }

  return {
    getData,
    getCurrentValue,
  };
})();

export const SyndeticsConfigProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [contextValue, setContextValue] =
    useState<SyndeticsConfigContextType | null>(
      SyndeticsConfigHolder.getCurrentValue()
    );
  useEffect(() => {
    (async () => {
      const data = await SyndeticsConfigHolder.getData();
      setContextValue(data);
    })();
  }, []);

  return (
    <SyndeticsConfigContext.Provider value={contextValue}>
      {children}
    </SyndeticsConfigContext.Provider>
  );
};
